import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import './Overlay.scss';

import { TestMetadata } from '../types';

export interface OverlayProps {
  className?: string;
  // token to control opacity (dark default)
  type?: 'light' | 'dark';
  // controls whether or not the overlay is visible (false default)
  isVisible?: boolean;
  // func that gets called when the overlay is clicked
  onOverlayClick?: () => void;
  // should isVisible be set to false if clicked (false default) - click on outside
  hideOnClick?: boolean;
  // Test IDs for component sections
  testMetadata?: TestMetadata;
}

const Overlay: FC<OverlayProps> = ({
  children,
  type = 'dark',
  isVisible = true,
  onOverlayClick,
  hideOnClick = false,
  className,
  testMetadata = { 'data-testid': 'overlay' },
  ...otherProps
}) => {
  const overlayClassNames = useMemo(
    () =>
      classNames('sb-overlay', className, {
        'sb-overlay_dark': type === 'dark',
        'sb-overlay_light': type === 'light',
      }),
    [className, type],
  );

  const [visible, setVisible] = useState<boolean>(isVisible);

  useEffect(() => {
    setVisible(isVisible);
  }, [isVisible]);

  const handleClick = useCallback(() => {
    if (hideOnClick) setVisible(!visible);

    onOverlayClick && onOverlayClick();
  }, [hideOnClick, visible, setVisible, onOverlayClick]);

  return visible ? (
    <div
      {...otherProps}
      className={overlayClassNames}
      onClick={handleClick}
      {...testMetadata}
    >
      {children}
    </div>
  ) : (
    <></>
  );
};

export default Overlay;
